/**
 * DashboardLayout.tsx — Authenticated dashboard shell
 *
 * Purpose:    Sidebar nav + content area for all 7 ɳSentry plugin dashboards.
 * Inputs:     child routes via <Outlet />
 * Outputs:    Sidebar + routed dashboard content
 * Constraints: No SSR; client-side SPA only
 * SOT:        T-P3-E5-W2-S4-T01
 */

import { NavLink, Outlet } from 'react-router-dom'

const NAV_ITEMS = [
  { to: '/dashboard/uptime', label: 'Uptime Monitor', icon: '●' },
  { to: '/dashboard/status', label: 'Status Page', icon: '◆' },
  { to: '/dashboard/incidents', label: 'Incidents', icon: '⚡' },
  { to: '/dashboard/alerts', label: 'Alert Router', icon: '🔔' },
  { to: '/dashboard/slo', label: 'SLO Tracker', icon: '◎' },
  { to: '/dashboard/synthetic', label: 'Synthetic Monitor', icon: '▷' },
  { to: '/dashboard/rum', label: 'RUM', icon: '◉' },
] as const

export function DashboardLayout() {
  return (
    <div className="flex min-h-screen bg-brand-bg">
      {/* Sidebar */}
      <aside className="hidden w-56 flex-shrink-0 border-r border-brand-subtle bg-brand-surface lg:flex lg:flex-col">
        <div className="flex h-14 items-center border-b border-brand-subtle px-4">
          <span className="text-sm font-semibold text-sky-700 dark:text-sky-400">ɳSentry</span>
        </div>
        <nav className="flex-1 space-y-0.5 px-2 py-4" aria-label="Dashboard navigation">
          {NAV_ITEMS.map(({ to, label, icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                [
                  'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-sky-900/50 text-sky-700 dark:text-sky-300'
                    : 'text-brand-muted hover:bg-brand-surface-2 hover:text-white',
                ].join(' ')
              }
            >
              <span aria-hidden="true" className="text-xs">{icon}</span>
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-brand-subtle px-4 py-3">
          <a
            href="/"
            className="text-xs text-brand-muted hover:text-brand-secondary"
          >
            ← Back to nSentry
          </a>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  )
}
